import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTenant } from '../../app/tenant-context';
import { getBooking, getPriceList, getPriceTable, getQuote, type Selection } from '../../shared/api/rpc';
import { toApiError } from '../../shared/api/errors';
import { areaByCode } from '../../shared/areas';
import { priceListFromTable, quoteFromTable } from '../../shared/price-table';

const PRICES_STALE = 5 * 60_000;

/**
 * Таблица цен точки — один запрос при открытии сайта (TenantApp запрашивает её вместе с точкой).
 * Дальше калькулятор, шаги записи и очередь считают итог из неё без запроса на каждое нажатие.
 */
export function priceTableQuery(slug: string) {
  return {
    queryKey: ['price-table', slug] as const,
    queryFn: () => getPriceTable(slug),
    staleTime: PRICES_STALE,
  };
}

export function usePriceTable() {
  const { slug } = useTenant();
  return useQuery(priceTableQuery(slug));
}

/**
 * Итог и длительность выбора. Цены посчитал сервер (таблица цен), здесь — только сумма; чего нет
 * в таблице (недоступная услуга, перенос записи, старый сервер без price_table) — RPC quote.
 */
export function useQuote(selection: Selection | null) {
  const { slug, tenant } = useTenant();
  const table = usePriceTable();
  const local = useMemo(
    () => (table.data && selection ? quoteFromTable(table.data, tenant, selection) : null),
    [table.data, tenant, selection],
  );
  return useQuery({
    queryKey: ['quote', slug, selection],
    queryFn: () => local ?? getQuote(slug, selection as Selection),
    initialData: local ?? undefined,
    // таблица ещё грузится — ждём её, а не спрашиваем quote отдельно
    enabled: selection !== null && !table.isPending,
    placeholderData: (prev) => prev,
    staleTime: PRICES_STALE,
  });
}

/**
 * Цены всех услуг раздела для выбранного типа авто — из таблицы цен, без неё — RPC price_list.
 * Радиус нужен только шиномонтажу; у аренды прайса нет — запрос не выполняется. area null —
 * «Шиномонтаж».
 */
export function usePriceList(vehicleClass: string | null, radius: number | null, area: string | null) {
  const { slug, tenant } = useTenant();
  const table = usePriceTable();
  const kind = area ? (areaByCode(tenant, area)?.kind ?? null) : 'tires';
  const needsRadius = kind === 'tires';
  const r = needsRadius ? radius : null;
  const local = useMemo(
    () => (table.data && vehicleClass ? priceListFromTable(table.data, tenant, vehicleClass, r, area) : null),
    [table.data, tenant, vehicleClass, r, area],
  );
  return useQuery({
    queryKey: ['price-list', slug, area, vehicleClass, r],
    queryFn: () => local ?? getPriceList(slug, vehicleClass as string, r, area),
    initialData: local ?? undefined,
    enabled:
      Boolean(vehicleClass) && kind !== null && kind !== 'rental' && (!needsRadius || radius !== null) && !table.isPending,
    staleTime: PRICES_STALE,
    placeholderData: (prev) => prev,
  });
}

/** Ключ идемпотентности: uuid v4 (crypto.randomUUID есть не во всех старых браузерах). */
export function newIdempotencyKey(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Запись по токену; сетевые ошибки повторяются, «не найдена» — нет. */
export function useBooking(token: string | undefined) {
  const { slug } = useTenant();
  return useQuery({
    queryKey: ['booking', slug, token],
    queryFn: () => getBooking(slug, token as string),
    enabled: Boolean(token),
    // вернулись из Telegram после привязки — обновить «напоминание подключено»
    refetchOnWindowFocus: true,
    retry: (count, error) => toApiError(error).code === 'network' && count < 2,
  });
}
